import type { PopoverPosition } from "@mui/material";
import { Alert, Box, Button, Stack } from "@mui/material";
import type { Ref } from "react";
import { useEffect, useEffectEvent, useImperativeHandle, useRef, useState } from "react";
import type {
  ArtifactPickedPart,
  ArtifactSourceState,
  ArtifactThemeMode,
  ArtifactTrailStep,
  ErrorMessage,
  HostMessage,
} from "@entrilia/shared/artifactRuntime/protocol";
import { isRuntimeMessage } from "@entrilia/shared/artifactRuntime/protocol";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import type { SmartViewArtifactVersion } from "../../../../../api/types/smartViewArtifactsTypes";
import type { ArtifactSourceStore } from "./artifactSourceStore";
import type { ArtifactRuntimeError } from "./checkResult";
import { toRuntimeError } from "./checkResult";
import { toAnchorPosition } from "./figureClick";
import { createInitMessage } from "./frameInit";
import { removeErrorCode } from "./runErrorMessage";
import type { SourceFigureTarget, SourceNote, SourceTarget } from "./sourceRequestsState";

export interface ArtifactScreen {
  view: string;
  params: Record<string, string[]>;
  sourceNotes: SourceNote[];
}

export interface ArtifactFrameHandle {
  refresh: () => void;
  listActiveTargets: () => SourceTarget[];
  getScreen: () => ArtifactScreen | undefined;
}

interface Props {
  ref?: Ref<ArtifactFrameHandle>;
  sourceStore: ArtifactSourceStore;
  artifactId: string;
  version: SmartViewArtifactVersion;
  filterValues: Record<string, string[]>;
  filterLabels: Record<string, Record<string, string>>;
  mode: ArtifactThemeMode;
  logo?: string;
  isViewer?: boolean;
  drillMode: boolean;
  pickMode: boolean;
  onSetFilter: (key: string, values: string[]) => void;
  onClearFilter: (key: string) => void;
  onRuntimeError?: (version: number, error: ArtifactRuntimeError) => void;
  onFigureClick: (version: number, target: SourceFigureTarget, anchorPosition: PopoverPosition) => void;
  onPreviewCaptured?: (artifactId: string, version: number, image: Blob) => void;
  onPartPicked: (version: number, part: ArtifactPickedPart, keepPicking: boolean) => void;
  onPickCancelled: () => void;
}

interface ShownRuntimeError {
  message: string;
  view: string;
}

type FilterChangeHandler = (values: Record<string, string[]>, labels: Record<string, Record<string, string>>) => void;

const MAX_ERROR_LENGTH = 4096;
const READY_TIMEOUT_MS = 60_000;

const mergeTrailParams = (trail: ArtifactTrailStep[]): Record<string, string[]> =>
  trail.reduce<Record<string, string[]>>((params, step) => ({ ...params, ...step.params }), {});

interface ViewerErrorPanelProps {
  onBackToDashboard?: () => void;
}

const ViewerErrorPanel = ({ onBackToDashboard }: ViewerErrorPanelProps) => (
  <Stack spacing={2} sx={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
    <DataLoadingFailed
      title={onBackToDashboard ? "This view could not be shown" : "This dashboard could not be shown"}
      text="Please try again later or contact the person who manages it."
      sx={{ flex: "none" }}
    />
    {onBackToDashboard && (
      <Button variant="outlined" onClick={onBackToDashboard}>
        Back to the dashboard
      </Button>
    )}
  </Stack>
);

interface LoadFailedPanelProps {
  onRetry: () => void;
}

const LoadFailedPanel = ({ onRetry }: LoadFailedPanelProps) => (
  <Stack spacing={2} sx={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
    <DataLoadingFailed
      title="This dashboard could not be loaded"
      text="Please try again. If it keeps happening, reload the page."
      sx={{ flex: "none" }}
    />
    <Button variant="outlined" onClick={onRetry}>
      Try again
    </Button>
  </Stack>
);

const ArtifactFrame = ({
  ref,
  sourceStore,
  artifactId,
  version,
  filterValues,
  filterLabels,
  mode,
  logo,
  isViewer,
  drillMode,
  pickMode,
  onSetFilter,
  onClearFilter,
  onRuntimeError,
  onFigureClick,
  onPreviewCaptured,
  onPartPicked,
  onPickCancelled,
}: Props) => {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const changeFiltersRef = useRef<FilterChangeHandler>(undefined);
  const changeModeRef = useRef<(mode: ArtifactThemeMode) => void>(undefined);
  const changeDrillModeRef = useRef<(enabled: boolean) => void>(undefined);
  const changePickModeRef = useRef<(enabled: boolean) => void>(undefined);
  const refreshRef = useRef<() => void>(undefined);
  const listActiveTargetsRef = useRef<() => SourceTarget[]>(undefined);
  const getScreenRef = useRef<() => ArtifactScreen | undefined>(undefined);
  const goHomeRef = useRef<() => void>(undefined);
  const requestPreviewRef = useRef<() => void>(undefined);
  const isPreviewRequestedRef = useRef(false);
  const [runtimeError, setRuntimeError] = useState<ShownRuntimeError>();
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [isLoadFailed, setIsLoadFailed] = useState(false);

  const createInit = useEffectEvent(() =>
    createInitMessage(version, filterValues, filterLabels, mode, { build: false, drillMode }, logo)
  );
  const isPickModeOn = useEffectEvent(() => pickMode);
  const handlePartPicked = useEffectEvent(onPartPicked);
  const handlePickCancelled = useEffectEvent(onPickCancelled);
  const handleSetFilter = useEffectEvent(onSetFilter);
  const handleClearFilter = useEffectEvent(onClearFilter);
  const handleFigureClick = useEffectEvent(onFigureClick);
  const handleRuntimeError = useEffectEvent((message: ErrorMessage) => {
    if (!isViewer || !message.contained) {
      setRuntimeError({ message: message.message.slice(0, MAX_ERROR_LENGTH), view: message.view });
    }
    onRuntimeError?.(message.version, toRuntimeError(version, message));
  });
  const isPreviewWanted = useEffectEvent(() => onPreviewCaptured !== undefined);
  const handlePreviewCaptured = useEffectEvent((capturedVersion: number, image: Blob) =>
    onPreviewCaptured?.(artifactId, capturedVersion, image)
  );
  const toShownSourceState = useEffectEvent((state: ArtifactSourceState): ArtifactSourceState =>
    isViewer && state.status === "error" ? { ...state, message: removeErrorCode(state.message) } : state
  );

  useImperativeHandle(
    ref,
    () => ({
      refresh: () => refreshRef.current?.(),
      listActiveTargets: () => listActiveTargetsRef.current?.() ?? [],
      getScreen: () => getScreenRef.current?.(),
    }),
    []
  );

  useEffect(() => {
    const frameWindow = frameRef.current?.contentWindow;
    if (!frameWindow) {
      return;
    }
    const versionNumber = version.version;
    const initial = createInit();
    let isInitialized = false;
    let isOnLightMainView = false;
    let isPreviewPending = false;
    let currentView: { view: string; trail: ArtifactTrailStep[] } | undefined;

    const readyTimer = setTimeout(() => {
      if (loadAttempt === 0) {
        setLoadAttempt(1);
      } else {
        setIsLoadFailed(true);
      }
    }, READY_TIMEOUT_MS);

    const post = (message: HostMessage): void => {
      frameWindow.postMessage(message, location.origin);
    };

    const client = sourceStore.connect({
      artifactId,
      version: versionNumber,
      sources: initial.sources,
      filterValues: initial.filterValues,
      onSourceState: (subscriptionId, state) =>
        post({ type: "sourceState", version: versionNumber, subscriptionId, state: toShownSourceState(state) }),
    });

    changeFiltersRef.current = (values, labels) => {
      if (isInitialized) {
        post({ type: "filtersChanged", version: versionNumber, values, labels });
      }
      client.changeFilterValues(values);
    };

    changeModeRef.current = (nextMode) => {
      if (isInitialized) {
        post({ type: "themeChanged", version: versionNumber, mode: nextMode });
      }
    };

    changeDrillModeRef.current = (enabled) => {
      if (isInitialized) {
        post({ type: "drillModeChanged", version: versionNumber, enabled });
      }
      client.setDrillMode(enabled);
    };

    changePickModeRef.current = (enabled) => {
      if (isInitialized) {
        post({ type: "pickModeChanged", version: versionNumber, enabled });
      }
    };

    refreshRef.current = client.refresh;
    listActiveTargetsRef.current = client.listActiveTargets;
    getScreenRef.current = () =>
      currentView && {
        view: currentView.view,
        params: mergeTrailParams(currentView.trail),
        sourceNotes: client.listSourceNotes(),
      };

    goHomeRef.current = () => {
      if (isInitialized) {
        post({ type: "openView", version: versionNumber, viewKey: "", params: {} });
      }
    };

    const requestPreview = (): void => {
      if (!isInitialized || !isOnLightMainView || isPreviewRequestedRef.current || !isPreviewWanted()) {
        return;
      }
      isPreviewRequestedRef.current = true;
      isPreviewPending = true;
      post({ type: "capturePreview", version: versionNumber });
    };
    requestPreviewRef.current = requestPreview;

    const handleMessage = (event: MessageEvent): void => {
      if (event.source !== frameWindow || event.origin !== location.origin || !isRuntimeMessage(event.data)) {
        return;
      }
      const message = event.data;
      if (message.type === "ready") {
        clearTimeout(readyTimer);
        client.reset();
        isInitialized = true;
        post(createInit());
        if (isPickModeOn()) {
          post({ type: "pickModeChanged", version: versionNumber, enabled: true });
        }
        return;
      }
      if (message.version !== versionNumber) {
        return;
      }
      switch (message.type) {
        case "subscribe":
          client.subscribe(message.subscriptionId, message.key, message.overrides);
          return;
        case "unsubscribe":
          client.unsubscribe(message.subscriptionId);
          return;
        case "setFilter":
          handleSetFilter(message.key, message.values);
          return;
        case "clearFilter":
          handleClearFilter(message.key);
          return;
        case "error":
          handleRuntimeError(message);
          return;
        case "viewChanged":
          currentView = { view: message.view, trail: message.trail };
          return;
        case "figureClicked": {
          const target = client.getFigureTarget(message.subscriptionId, message.row, message.columnKey);
          const frameElement = frameRef.current;
          if (target && frameElement) {
            handleFigureClick(
              versionNumber,
              target,
              toAnchorPosition(message.anchor, frameElement.getBoundingClientRect())
            );
          }
          return;
        }
        case "partPicked":
          if (isPickModeOn()) {
            handlePartPicked(versionNumber, message.part, message.keepPicking);
          }
          return;
        case "pickCancelled":
          handlePickCancelled();
          return;
        case "rendered":
          isOnLightMainView = message.view === "" && message.mode === "light";
          requestPreview();
          return;
        case "previewCaptured":
          if (isPreviewPending) {
            isPreviewPending = false;
            if (message.image) {
              handlePreviewCaptured(versionNumber, message.image);
            }
          }
          return;
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
      clearTimeout(readyTimer);
      changeFiltersRef.current = undefined;
      changeModeRef.current = undefined;
      changeDrillModeRef.current = undefined;
      changePickModeRef.current = undefined;
      refreshRef.current = undefined;
      listActiveTargetsRef.current = undefined;
      getScreenRef.current = undefined;
      goHomeRef.current = undefined;
      requestPreviewRef.current = undefined;
      client.close();
    };
  }, [artifactId, version.version, sourceStore, loadAttempt]);

  useEffect(() => {
    changeFiltersRef.current?.(filterValues, filterLabels);
  }, [filterValues, filterLabels]);

  useEffect(() => {
    changeModeRef.current?.(mode);
  }, [mode]);

  useEffect(() => {
    changeDrillModeRef.current?.(drillMode);
  }, [drillMode]);

  useEffect(() => {
    changePickModeRef.current?.(pickMode);
  }, [pickMode]);

  useEffect(() => {
    if (onPreviewCaptured) {
      requestPreviewRef.current?.();
    }
  }, [onPreviewCaptured]);

  const handleBackToDashboardClick = () => {
    goHomeRef.current?.();
    setRuntimeError(undefined);
  };

  const handleRetryClick = () => {
    setIsLoadFailed(false);
    setLoadAttempt((attempt) => attempt + 1);
  };

  const canvasBackground = version.style?.[mode].background ?? "background.default";
  const isFrameHidden = isLoadFailed || (isViewer && runtimeError !== undefined);

  return (
    <Stack sx={{ flex: 1, minHeight: 0 }}>
      {runtimeError && !isViewer && (
        <Alert severity="error" sx={{ mx: 3, mt: 2 }}>
          {runtimeError.message}
        </Alert>
      )}
      {runtimeError && isViewer && (
        <ViewerErrorPanel onBackToDashboard={runtimeError.view === "" ? undefined : handleBackToDashboardClick} />
      )}
      {isLoadFailed && <LoadFailedPanel onRetry={handleRetryClick} />}
      <Box
        component="iframe"
        key={loadAttempt}
        ref={frameRef}
        src="/artifact-frame.html"
        title="Dashboard"
        sx={{
          flex: 1,
          width: "100%",
          border: 0,
          bgcolor: canvasBackground,
          // A dark-scheme page paints an opaque white canvas under a frame whose own document uses the light scheme.
          colorScheme: "light",
          display: isFrameHidden ? "none" : "block",
        }}
      />
    </Stack>
  );
};

export default ArtifactFrame;
